import { groupBy, keys, mapValues } from "lodash";
import { OrderItem, OrderItemStatus, OrderItems, SizeStock, StockByProductId, StockByType, StockScope, StockStatus } from "../types";
import { ErrorCode, ErrorItem } from "../types/Error";

export const isProductStockHidden = (productsStock: StockByProductId, productId: string, sizeId: string, menuId: string, quantityToAdd = 0) => {
  const productStock = productsStock?.[productId];

  if (productStock?.stockScope !== StockScope.All && productStock?.enabledMenus?.[menuId]?.enabled !== true) return false;

  const stockItem: SizeStock = productsStock?.[productId]?.sizes?.[sizeId];
  if (stockItem && stockItem.enabled && stockItem.status === StockStatus.Hide) {
    if (!stockItem.isStockLimitEnabled) {
      return true;
    } else if ((stockItem.limit || 0) <= (stockItem.sold || 0) + quantityToAdd) {
      return true;
    }
  }

  return false;
};

export const isProductStockSoldOut = (productsStock: StockByProductId, productId: string, sizeId: string, menuId: string, quantityToAdd = 0) => {
  const productStock = productsStock?.[productId];

  if (productStock?.stockScope !== StockScope.All && productStock?.enabledMenus?.[menuId]?.enabled !== true) return false;

  const stockItem: SizeStock = productsStock?.[productId]?.sizes?.[sizeId];

  if (stockItem && stockItem.enabled && stockItem.status === StockStatus.SoldOut) {
    if (!stockItem.isStockLimitEnabled) {
      return true;
    } else if ((stockItem.limit || 0) <= (stockItem.sold || 0) + quantityToAdd) {
      return true;
    }
  }

  return false;
};

export function checkOrderItemStockError(productsStock: StockByProductId, productId: string, sizeId: string, productName: string, menuId: string, quantityToAdd = 0): ErrorItem | null {
  let error: ErrorItem = null;
  const productStockLimit = productsStock && productsStock?.[productId] ? productsStock[productId] : null;
  const sizeStockItem: SizeStock = productStockLimit && productStockLimit?.sizes?.[sizeId] ? productStockLimit?.sizes?.[sizeId] : null;

  if (sizeStockItem && sizeStockItem.enabled) {
    if (productStockLimit?.stockScope !== StockScope.All && productStockLimit?.enabledMenus?.[menuId]?.enabled !== true) return error;

    if (isProductStockHidden(productsStock, productId, sizeId, menuId, quantityToAdd)) {
      error = { code: ErrorCode.PRODUCT_STOCK_HIDDEN, message: `${productName} is no longer available.` };
    } else if (isProductStockSoldOut(productsStock, productId, sizeId, menuId, quantityToAdd)) {
      error = { code: ErrorCode.PRODUCT_STOCK_SOLD_OUT, message: `${productName} is sold out.` };
    }
  }
  return error;
}

export const validateBasketStock = (stockByType: StockByType, orderItems: OrderItem[], menuId: string): ErrorItem | null => {
  let error: ErrorItem = null;
  const filteredItems = orderItems.filter((oi) => oi.orderStatus === OrderItemStatus.InBasket && oi.quantity !== oi.paidQuantity);

  for (let index = 0; index < filteredItems.length; index++) {
    const orderItem = filteredItems[index];
    error = checkOrderItemStockError(orderItem.isSpecialProduct ? stockByType.specials : stockByType.products, orderItem.productId, orderItem.sizeId, orderItem.name, menuId, orderItem.quantity - 1);
    /** Find any stock limit error inside combo items  */
    if (!error && keys(orderItem.comboGroup).length > 0) {
      const comboGroup = orderItem.comboGroup;
      for (const productId in comboGroup) {
        const comboOrderItem = comboGroup[productId];
        error = checkOrderItemStockError(orderItem.isSpecialProduct ? stockByType.specials : stockByType.products, productId, comboOrderItem.sizeId, comboOrderItem.name, menuId, comboOrderItem.isComboQuantityIndependentFromOrderItem ? comboOrderItem.quantity - 1 : comboOrderItem.quantity * orderItem.quantity);
        if (error) break;
      }
    }
    if (error) break;
  }
  return error;
};

export const getProductStockInfo = (productsStock: StockByProductId, productId: string, sizeId: string, menuId: string) => {
  const isHidden = isProductStockHidden(productsStock, productId, sizeId, menuId);
  const isSoldOut = isProductStockSoldOut(productsStock, productId, sizeId, menuId);
  const isStockLimitEnabled = productsStock?.[productId]?.sizes?.[sizeId]?.enabled && productsStock?.[productId]?.sizes?.[sizeId]?.isStockLimitEnabled;
  let limit,
    sold = null;
  if (isStockLimitEnabled) {
    limit = productsStock?.[productId]?.sizes?.[sizeId]?.limit || 0;
    sold = productsStock?.[productId]?.sizes?.[sizeId]?.sold || 0;
  }
  return { isHidden, isSoldOut, isStockLimitEnabled, limit, sold };
};

export const isStockLimitReached = (totalOrderedQuantity: number, stockByType: StockByType, productId: string, sizeId: string, menuId: string) => {
  const isSpecial = isFromSpecialProduct(stockByType, productId, menuId);
  const productStock = isSpecial ? stockByType?.specials?.[productId] : stockByType?.products?.[productId];
  const sizeStock = productStock?.sizes?.[sizeId];

  if (productStock?.stockScope !== StockScope.All && !productStock?.enabledMenus?.[menuId]?.enabled) return false;
  if (sizeStock?.enabled && sizeStock?.isStockLimitEnabled) {
    return totalOrderedQuantity >= (sizeStock.limit || 0) - (sizeStock.sold || 0);
  }
  return false;
};

export const isFromSpecialProduct = (stockByType: StockByType, productId: string, menuId: string) => {
  const productStock = stockByType?.specials?.[productId];
  if (!productStock) return false;
  if (productStock?.stockScope !== StockScope.All && !productStock?.enabledMenus?.[menuId]?.enabled) return false;

  return true;
};

export const transformOrderItemsForStockUpdate = (orderItems: OrderItems): { [productId: string]: { [sizeId: string]: number } } => {
  const arr = Object.values(orderItems);

  // Group by productId
  const groupedByProduct = groupBy(arr, "productId");

  // Reduce each product group into { sizeId: quantity }
  return mapValues(groupedByProduct, (items) =>
    items.reduce((acc, { sizeId, quantity }) => {
      acc[sizeId] = (acc[sizeId] || 0) + quantity;
      return acc;
    }, {})
  );
};
